import React, { useRef, useEffect } from 'react'
import { Link } from 'gatsby'
import { resolveInternalRedirect } from '../../utils/functions'

const Level0 = ({items, currentNodeId, currentPathName, currentLevel}) => {
	const navRef = useRef(null)

	// Boucle clavier : Tab en fin de liste → premier lien, Shift+Tab en début → dernier lien
	useEffect(() => {
		const nav = navRef.current
		if (!nav) return

		const getFocusables = () =>
			Array.from(nav.querySelectorAll('a[href]')).filter(
				el => el.offsetParent !== null
			)

		const handleKeydown = (e) => {
			const current = document.activeElement
			if (!nav.contains(current)) return

			// Espace et Entrée : activer le lien comme un clic (affiche le sous-menu / navigue)
			if (e.key === ' ' || e.key === 'Enter') {
				const link = current.closest('.list-menu-link') || (current.matches('a') ? current : null)
				if (link && nav.contains(link)) {
					e.preventDefault()
					try {
						sessionStorage.setItem('menu-focus-submenu', '1')
						const href = link.getAttribute('href') || link.getAttribute('to') || ''
						if (href) sessionStorage.setItem('menu-parent-link', href)
					} catch (err) {}
					link.click()
				}
				return
			}

			if (e.key !== 'Tab') return
			const focusables = getFocusables()
			if (focusables.length === 0) return
			const index = focusables.indexOf(current)
			if (index === -1) return

			if (e.shiftKey) {
				if (index === 0) {
					e.preventDefault()
					focusables[focusables.length - 1].focus()
				}
			} else {
				if (index === focusables.length - 1) {
					e.preventDefault()
					focusables[0].focus()
				}
			}
		}

		nav.addEventListener('keydown', handleKeydown)
		return () => nav.removeEventListener('keydown', handleKeydown)
	}, [items])

	// Fonction pour décoder les entités HTML
	const decodeHtmlEntities = (text) => {
		if (typeof text !== 'string') return text
		
		// Vérifier si on est côté client (navigateur)
		if (typeof window !== 'undefined' && typeof document !== 'undefined') {
			const textarea = document.createElement('textarea')
			textarea.innerHTML = text
			return textarea.value
		}
		
		// Fallback côté serveur - décodage basique des entités HTML courantes
		return text
			.replace(/&amp;/g, '&')
			.replace(/&lt;/g, '<')
			.replace(/&gt;/g, '>')
			.replace(/&quot;/g, '"')
			.replace(/&#039;/g, "'")
			.replace(/&nbsp;/g, ' ')
	}

	// hide_menu peut venir de GraphQL en boolean, number (0/1), string ("0"/"1"/"true") ou sous le nom hideMenu
	const isHiddenFromMenu = (val) =>
		val === true || val === 1 || val === '1' || (typeof val === 'string' && val.toLowerCase() === 'true')
	const getHideMenu = (node) => node.hide_menu !== undefined ? node.hide_menu : node.hideMenu

	// Filtrer : n'afficher que les éléments non cachés du menu (hide_menu respecté strictement)
	const visibleItems = items.filter((item) => !isHiddenFromMenu(getHideMenu(item)))

	// Si aucun élément à afficher, retourner null
	if (visibleItems.length === 0) {
		return null
	}

	return (
		<nav ref={navRef} className="menu-level-0" aria-label="Menu de navigation principal">
			<ul className="list-menu list-unstyled" role="list">
				{visibleItems.map((item) => {
					const { full_slug, title, content_page, wordpress_parent } = item
					// Déterminer si l'élément est actif
					const isActive = content_page && full_slug === currentPathName
					// Décoder le titre pour corriger l'affichage des entités HTML
					const decodedTitle = decodeHtmlEntities(title)
					return (
						<li key={full_slug} className="list-menu-item pl-3 pr-4">
							<Link 
								className={`list-menu-link ${(content_page ? 'content-page' : '')} ${isActive ? 'active' : ''}`}
								to={resolveInternalRedirect(full_slug)}
								aria-current={isActive ? 'page' : undefined}
								aria-label={isActive ? `${decodedTitle} - Page actuelle` : `Aller à ${decodedTitle}`}
								state={{
									prevPathName: currentPathName, 
									prevLevel: currentLevel,
									menuNavigation: !content_page 
								}}
							>
								{decodedTitle}
							</Link>
						</li>
					)
				})}
			</ul>
		</nav>
	)
}

export default Level0
